import React, { useEffect, useRef, useState } from 'react'
import styles from './CustomScrollbar.module.scss'

const CustomScrollbar = ({ containerRef }) => {
  const [scrollLeft, setScrollLeft] = useState(0)
  const [scrollWidth, setScrollWidth] = useState(0)
  const [clientWidth, setClientWidth] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [startScrollLeft, setStartScrollLeft] = useState(0)
  const [isVisible, setIsVisible] = useState(false)

  const thumbRef = useRef(null)
  const trackRef = useRef(null)
  const animationRef = useRef(null)

  const updateScrollData = () => {
    if (containerRef?.current) {
      const newScrollWidth = containerRef.current.scrollWidth
      const newClientWidth = containerRef.current.clientWidth
      const newScrollLeft = containerRef.current.scrollLeft

      setScrollWidth(newScrollWidth)
      setClientWidth(newClientWidth)
      setScrollLeft(newScrollLeft)
      setIsVisible(newScrollWidth > newClientWidth)
    }
  }

  const smoothScrollTo = (targetScrollLeft) => {
    if (!containerRef?.current) return

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
    }

    const startScroll = containerRef.current.scrollLeft
    const distance = targetScrollLeft - startScroll
    const duration = 300
    const startTime = performance.now()

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)

      const easeInOutCubic =
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2

      const newScrollLeft = startScroll + distance * easeInOutCubic
      containerRef.current.scrollLeft = newScrollLeft
      setScrollLeft(newScrollLeft)

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate)
      }
    }

    animationRef.current = requestAnimationFrame(animate)
  }

  const handleContainerScroll = () => {
    if (containerRef?.current && !isDragging) {
      setScrollLeft(containerRef.current.scrollLeft)
    }
  }

  const handleThumbMouseDown = (e) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()

    setIsDragging(true)
    setStartX(e.clientX)
    setStartScrollLeft(scrollLeft)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'grabbing'
  }

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef?.current) return

    const deltaX = e.clientX - startX
    const trackWidth = trackRef.current?.offsetWidth || 0
    const thumbWidth = thumbRef.current?.offsetWidth || 0
    const maxThumbTranslate = trackWidth - thumbWidth

    if (maxThumbTranslate <= 0) return

    const scrollRatio = deltaX / maxThumbTranslate
    const maxScrollLeft = scrollWidth - clientWidth
    const newScrollLeft = Math.max(
      0,
      Math.min(startScrollLeft + scrollRatio * maxScrollLeft, maxScrollLeft),
    )

    containerRef.current.scrollLeft = newScrollLeft
    setScrollLeft(newScrollLeft)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
  }

  const handleTrackClick = (e) => {
    if (!containerRef?.current || !trackRef.current || !thumbRef.current) return

    if (e.target === thumbRef.current) return

    const trackRect = trackRef.current.getBoundingClientRect()
    const thumbWidth = thumbRef.current.offsetWidth
    const clickX = e.clientX - trackRect.left

    let newThumbLeft = clickX - thumbWidth / 2

    const trackWidth = trackRect.width
    newThumbLeft = Math.max(0, Math.min(newThumbLeft, trackWidth - thumbWidth))

    const scrollRatio = newThumbLeft / (trackWidth - thumbWidth)
    const maxScrollLeft = scrollWidth - clientWidth
    const targetScrollLeft = scrollRatio * maxScrollLeft

    smoothScrollTo(targetScrollLeft)
  }

  const handleTrackWheel = (e) => {
    if (!containerRef?.current) return

    e.preventDefault()
    const delta = e.deltaY || e.deltaX
    const newScrollLeft = Math.max(
      0,
      Math.min(scrollLeft + delta, scrollWidth - clientWidth),
    )

    smoothScrollTo(newScrollLeft)
  }

  const getThumbPosition = () => {
    if (scrollWidth <= clientWidth) return 0

    const scrollPercent = scrollLeft / (scrollWidth - clientWidth)
    const trackWidth = trackRef.current?.offsetWidth || 0
    const thumbWidth = thumbRef.current?.offsetWidth || 0
    const maxThumbTranslate = trackWidth - thumbWidth

    return scrollPercent * maxThumbTranslate
  }

  const getThumbWidth = () => {
    if (scrollWidth <= clientWidth) return 0

    const trackWidth = trackRef.current?.offsetWidth || 0
    const minThumbWidth = 40
    const calculatedWidth = (clientWidth / scrollWidth) * trackWidth

    return Math.max(minThumbWidth, calculatedWidth)
  }

  useEffect(() => {
    if (!containerRef?.current) return

    updateScrollData()

    const observer = new MutationObserver(updateScrollData)
    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    })

    containerRef.current.addEventListener('scroll', handleContainerScroll)
    window.addEventListener('resize', updateScrollData)

    // Для плавного обновления requestAnimationFrame
    let rafId
    const updateOnFrame = () => {
      updateScrollData()
      rafId = requestAnimationFrame(updateOnFrame)
    }
    rafId = requestAnimationFrame(updateOnFrame)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateScrollData)
      if (containerRef?.current) {
        containerRef.current.removeEventListener(
          'scroll',
          handleContainerScroll,
        )
      }
      if (rafId) cancelAnimationFrame(rafId)
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [containerRef])

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)

      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, startX, startScrollLeft, scrollWidth, clientWidth])

  if (!isVisible) return null

  return (
    <div className={styles.customScrollbarContainer}>
      <div
        ref={trackRef}
        className={styles.customScrollbarTrack}
        onClick={handleTrackClick}
        onWheel={handleTrackWheel}
      >
        <div
          ref={thumbRef}
          className={`${styles.customScrollbarThumb} ${isDragging ? styles.dragging : ''}`}
          style={{
            transform: `translateX(${getThumbPosition()}px)`,
            width: `${getThumbWidth()}px`,
          }}
          onMouseDown={handleThumbMouseDown}
        />
      </div>
    </div>
  )
}

export default CustomScrollbar
